import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { FilePenLine, Search, Plus, Trash2, X } from "lucide-react";
import constant from "../../constant";

const DevicesTable = ({ toAdd, parent, devices, onEdit, onDelete }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false); // State for delete confirmation modal
  const [deviceToDelete, setDeviceToDelete] = useState(null); // Track device to delete
  const [availableDevices, setAvailableDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { hospitalId } = useParams(); // Get hospitalId from URL

  // Filter devices based on search query
  const filteredDevices = devices.filter((device) =>
    device.device_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Fetch available devices when modal opens
  useEffect(() => {
    if (isModalOpen) {
      fetchDevices();
    }
  }, [isModalOpen]);

  const fetchDevices = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setError("No authentication token found");
        return;
      }

      const response = await fetch(`${constant.REACT_APP_API_URL}/device/?type=unassigned`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const result = await response.json();
      if (response.ok) {
        setAvailableDevices(result);
        if (result.length > 0) {
          setSelectedDeviceId(result[0].device_id); // Set default selection
        }
      } else {
        setError(result.message || "Failed to fetch devices");
      }
    } catch (error) {
      setError("Error fetching devices");
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedDeviceId) {
      setError("Please select a device");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setError("No authentication token found");
        return;
      }

      const response = await fetch(
        `${constant.REACT_APP_API_URL}/device/assignment/${selectedDeviceId}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            hospital_id: hospitalId,
          }),
        }
      );

      const result = await response.json();
      if (response.ok) {
        setIsModalOpen(false);
        window.location.reload();
      } else {
        setError(result.message || "Failed to assign device");
      }
    } catch (error) {
      setError("Error assigning device");
    } finally {
      setLoading(false);
    }
  };

  // Handle delete button click to open confirmation modal
  const handleDeleteClick = (device) => {
    setDeviceToDelete(device);
    setIsDeleteModalOpen(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = () => {
    if (deviceToDelete) {
      onDelete(deviceToDelete.device_id);
      setIsDeleteModalOpen(false);
      setDeviceToDelete(null);
    }
  };

  // Handle delete cancel
  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
    setDeviceToDelete(null);
  };

  return (
    <div className="max-w-8xl mx-auto px-0 py-8">
      {/* Header Section */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Devices</h1>
            <p className="mt-1 text-xs text-gray-500">
              Total devices ({devices.length})
            </p>
          </div>
          <div className="flex gap-3">
            {toAdd ? (
              <button
                onClick={() => setIsModalOpen(true)}
                className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-md transition-colors duration-150"
              >
                <Plus size={18} className="mr-2" />
                Assign Device
              </button>
            ) : (
              <Link
                to="/admin/devices/add"
                className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-md transition-colors duration-150"
              >
                <Plus size={18} className="mr-2" />
                Add Device
              </Link>
            )}
          </div>
        </div>

        {/* Search Section */}
        <div className="relative w-full sm:w-96">
          <Search
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            size={20}
          />
          <input
            type="text"
            placeholder="Search devices..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-150"
          />
        </div>
      </div>

      {/* Table Section */}
      <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
        {filteredDevices.length === 0 ? (
          <>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-brand-600">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Device Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Assign Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Hospital Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
            </table>
            <div className="text-center py-8 text-gray-500 text-lg">
              No devices found
            </div>
          </>
        ) : (
          <div className="min-w-full overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-brand-600">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Device Name
                  </th>

                  {parent ? (
                    <>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Hospital Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Assign Status
                      </th>
                    </>
                  ) : (
                    ''
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>

                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredDevices.map((device) => (
                  <tr
                    key={device.device_id}
                    className="hover:bg-gray-50 transition-colors duration-150"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-600 capitalize">
                      {device.device_name}
                    </td>
                    {parent ? (
                      <>
                        <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-600">
                          {device.hospital_id == null ? "Not Assigned" : "Assigned"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-600">
                          {device.hospital_id ? device.hospital_name : "N/A"}
                        </td>
                      </>
                    ) : (
                      ''
                    )}
                    <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-600">
                      <div className="flex items-center space-x-3">
                        <Link
                          to={`/admin/devices/edit/${device.device_id}`}
                          className="text-blue-600 hover:text-blue-900 transition-colors duration-150"
                        >
                          <FilePenLine size={18} />
                        </Link>
                        <button
                          onClick={() => handleDeleteClick(device)}
                          className="text-red-600 hover:text-red-900 transition-colors duration-150"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal for Assigning Device */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-800">Assign Device</h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>

            {loading && (
              <div className="bg-blue-50 text-blue-700 p-4 rounded-lg mb-4">
                Processing...
              </div>
            )}
            {error && (
              <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-4 flex justify-between items-center">
                {error}
                <button
                  onClick={() => setError(null)}
                  className="text-sm text-blue-600 hover:text-blue-700"
                >
                  Clear
                </button>
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Device
              </label>
              <select
                value={selectedDeviceId}
                onChange={(e) => setSelectedDeviceId(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={loading}
              >
                {availableDevices.length === 0 ? (
                  <option value="">No devices available</option>
                ) : (
                  availableDevices.map((device) => (
                    <option key={device.device_id} value={device.device_id}>
                      {device.device_name}
                    </option>
                  ))
                )}
              </select>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleAssign}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                disabled={loading || !selectedDeviceId}
              >
                Assign
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal for Delete Confirmation */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-800">Confirm Deletion</h2>
              <button
                onClick={handleDeleteCancel}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to delete the device "
              {deviceToDelete?.device_name}"? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={handleDeleteCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DevicesTable;